import { Spin } from 'antd'
import { useCdfRawValues } from 'features/dataLayers'
import { FigureSelectionArea } from 'features/fde'
import { computeCriticalPath } from 'features/indicationTriage/cards/ffsCalc/calculations/calculateCtp'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { LABEL_RSFA } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFinalAcceptabilityCriteraSection'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import isNil from 'lodash/isNil'
import minBy from 'lodash/minBy'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { computeRsfs, Rsf, RsfTable } from './RsfTable'

export interface FfsCtpAverageCalcProps {
  isLoading: boolean
  area: FigureSelectionArea | undefined
  rsfs: ReadonlyArray<Rsf>
  minRsf: Rsf | undefined
  passesFac: boolean
  rsfa: GeckoMeasurement | null
  setRsfa: Dispatch<SetStateAction<GeckoMeasurement | null>>
}

type HookProps = {
  innerDiameter: GeckoMeasurement | null
  area: FigureSelectionArea | undefined
  tc: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  longitudinalAxis: 'x' | 'y'
  isLoading: boolean
}

export function useFfsCtpAverageCalcProps({
  area,
  tc,
  fcaMl,
  longitudinalAxis,
  isLoading,
  innerDiameter,
}: HookProps): FfsCtpAverageCalcProps {
  const { relevantThicknessCDF: cdf } = useIndicationData()
  const { buffer: cdfData, isLoading: cdfDataLoading } = useCdfRawValues(cdf)
  const criticalPath = useMemo(
    () =>
      cdf && cdfData && area && fcaMl
        ? computeCriticalPath(area, cdf, cdfData.values, longitudinalAxis).map(
            (point) => ({
              ...point,
              value: point.value
                ? GeckoMeasurement.CreateFromValue(
                    point.value.num - fcaMl.toNumber(point.value.unit),
                    point.value.unit
                  )
                : undefined,
            })
          )
        : [],
    [area, cdf, cdfData, fcaMl, longitudinalAxis]
  )

  const rsfs = useMemo(
    () =>
      !isNil(tc) && !isNil(fcaMl) && !isNil(innerDiameter)
        ? computeRsfs({
            criticalPath,
            tc,
            axis: longitudinalAxis,
            fca: fcaMl,
            innerDiameter,
          })
        : [],
    [criticalPath, fcaMl, innerDiameter, longitudinalAxis, tc]
  )

  const minRsf = minBy(
    rsfs,
    (rsf) =>
      rsf.rsf?.toNumber(GeckoMeasurementUnit.Ratio) ?? Number.POSITIVE_INFINITY
  )
  const [rsfa, setRsfa] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(0.9, GeckoMeasurementUnit.Ratio)
  )
  return useMemo(
    () => ({
      area,
      longitudinalAxis,
      criticalPath,
      isLoading: isLoading || cdfDataLoading,
      rsfs,
      minRsf,
      passesFac: !!minRsf?.rsf && !!rsfa && minRsf.rsf.num >= rsfa.num,
      rsfa,
      setRsfa,
    }),
    [
      area,
      cdfDataLoading,
      criticalPath,
      isLoading,
      longitudinalAxis,
      minRsf,
      rsfa,
      rsfs,
    ]
  )
}

const RawFfsCtpAverageCalc: FC<FfsCtpAverageCalcProps> = ({
  area,
  isLoading,
  rsfs,
  minRsf,
  rsfa,
  setRsfa,
}) => {
  return (
    <Spin spinning={isLoading}>
      <RsfTable rsfs={rsfs} />
      <SectionContainer>
        {area && (
          <>
            <FfsParameterField
              label={LABEL_RSFA}
              labelTooltip='Acceptable Remaining Strength Factor'
              labelWidth={LABEL_WIDTH}
              value={rsfa}
              setValue={setRsfa}
              measurementType='ratio'
              min={GeckoMeasurement.CreateFromValue(
                0,
                GeckoMeasurementUnit.Ratio
              )}
              max={GeckoMeasurement.CreateFromValue(
                1,
                GeckoMeasurementUnit.Ratio
              )}
              step={0.05}
            />
            <FfsParameterField
              label='RSF'
              labelTooltip='Minimum RSF from the CTP sections'
              labelWidth={LABEL_WIDTH}
              value={minRsf?.rsf ?? null}
              measurementType='ratio'
            />
          </>
        )}
      </SectionContainer>
    </Spin>
  )
}

export const FfsCtpAverageCalc = React.memo(RawFfsCtpAverageCalc)
